import React, { createContext, useContext, useState, useEffect } from 'react';
import { COLORS } from '../utils/colors';
import { getFontSizeForScreen } from '../utils/fontUtils';

export interface Theme {
  colors: {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    accent: string;
    accentYellow: string;
    accentBlue: string;
    white: string;
    black: string;
    overlay: string;
    overlayLight: string;
    overlayDark: string;
    reverseMainText: string;
  };
  fontSize: number;
}

export const lightTheme: Theme = {
  colors: {
    primary: COLORS['accent-primary'],
    secondary: COLORS['BG-light-overlay-darker-1'],
    background: COLORS['BG-light'],
    surface: COLORS['BG-light-overlay-lighter-1'],
    text: COLORS.black,
    textSecondary: COLORS['text-dark-50'],
    textTertiary: COLORS['element-dark-opacity-12'],
    accent: COLORS['accent-primary'],
    accentYellow: COLORS['accent-yellow'],
    accentBlue: COLORS['accent-blue'],
    white: COLORS.white,
    black: COLORS.black,
    overlay: COLORS['element-dark-opacity-8'],
    overlayLight: COLORS['element-dark-opacity-8'],
    overlayDark: COLORS['element-dark-opacity-12'],
    reverseMainText: COLORS.white,
  },
  fontSize: getFontSizeForScreen('h4'),
};

export const darkTheme: Theme = {
  colors: {
    primary: COLORS['accent-primary'],
    secondary: COLORS['book-intro-details-background'],
    background: COLORS['BG-dark'],
    surface: COLORS['book-intro-details-background'],
    text: COLORS.white,
    textSecondary: COLORS['text-light-85'],
    textTertiary: COLORS['text-light-50'],
    accent: COLORS['accent-primary'],
    accentYellow: COLORS['accent-yellow'],
    accentBlue: COLORS['accent-blue'],
    white: COLORS.white,
    black: COLORS.black,
    overlay: COLORS['element-opacity-8'],
    overlayLight: COLORS['text-light-30'],
    overlayDark: COLORS['text-light-50'],
    reverseMainText: COLORS.black,
  },
  fontSize: getFontSizeForScreen('h4'),
};

export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  reverseThemeMode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const savedTheme = localStorage.getItem('themeMode');
    return (savedTheme as ThemeMode) || 'dark';
  });

  const theme = themeMode === 'light' ? lightTheme : darkTheme;

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  };

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeMode,
        reverseThemeMode: themeMode === 'light' ? 'dark' : 'light',
        toggleTheme,
        setThemeMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
